import { Link } from "react-router-dom";
import { Phone, MessageCircle, Instagram, MapPin, Clock, Star } from "lucide-react";
import { BUSINESS, NAV_LINKS, WA_DEFAULT, IMAGES } from "@/data/business";
import { useBooking } from "@/components/booking";

export default function Footer() {
  const { openBooking } = useBooking();
  return (
    <footer data-testid="site-footer" className="grain relative bg-ink text-ivory">
      <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8 lg:py-20">
        <div className="grid gap-12 lg:grid-cols-[1.3fr_1fr_1.2fr_1fr]">
          <div>
            <div className="flex items-center gap-4">
              <img
                src={IMAGES.logo.src}
                alt={IMAGES.logo.alt}
                className="h-14 w-14 rounded-full border border-ivory/15 object-cover"
                loading="lazy"
              />
              <div>
                <p className="font-serif text-2xl font-medium italic leading-none">Glam Girl</p>
                <p className="mt-1 text-[10px] font-semibold uppercase tracking-[0.3em] text-champagne">
                  Makeup Studio
                </p>
              </div>
            </div>
            <p className="mt-5 max-w-xs text-sm leading-relaxed text-ivory/55">
              Ladies Salon · Hair &amp; Makeup Studio in {BUSINESS.area}. Refined beauty, close to home.
            </p>
            <div className="mt-5 flex items-center gap-2 text-sm text-champagne" data-testid="footer-rating">
              <Star className="h-4 w-4 fill-champagne" />
              <span className="font-semibold">{BUSINESS.rating}</span>
              <span className="text-ivory/45">· {BUSINESS.reviewCount} Google reviews</span>
            </div>
          </div>

          <nav aria-label="Footer">
            <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-champagne">Quick Links</p>
            <ul className="mt-5 space-y-3">
              {NAV_LINKS.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    data-testid={`footer-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className="text-sm text-ivory/65 transition-colors hover:text-champagne"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-champagne">Contact</p>
            <ul className="mt-5 space-y-4 text-sm text-ivory/65">
              <li className="flex gap-3">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-champagne/70" />
                <span>{BUSINESS.address}</span>
              </li>
              <li>
                <a href={BUSINESS.phoneTel} data-testid="footer-phone" className="flex gap-3 transition-colors hover:text-champagne">
                  <Phone className="mt-0.5 h-4 w-4 shrink-0 text-champagne/70" />
                  {BUSINESS.phoneDisplay}
                </a>
              </li>
              <li>
                <a href={WA_DEFAULT} target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp" className="flex gap-3 transition-colors hover:text-champagne">
                  <MessageCircle className="mt-0.5 h-4 w-4 shrink-0 text-champagne/70" />
                  {BUSINESS.whatsappDisplay}
                </a>
              </li>
              <li className="flex gap-3">
                <Clock className="mt-0.5 h-4 w-4 shrink-0 text-champagne/70" />
                {BUSINESS.hoursLine}
              </li>
              <li>
                <a href={BUSINESS.instagram} target="_blank" rel="noopener noreferrer" data-testid="footer-instagram" className="flex gap-3 transition-colors hover:text-champagne">
                  <Instagram className="mt-0.5 h-4 w-4 shrink-0 text-champagne/70" />
                  {BUSINESS.instagramHandle}
                </a>
              </li>
            </ul>
          </div>

          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-champagne">Book</p>
            <p className="mt-5 text-sm leading-relaxed text-ivory/55">
              Walk in, call, or send us a message — we'll find a time that suits you.
            </p>
            <button
              onClick={() => openBooking()}
              data-testid="footer-book-button"
              className="mt-5 w-full rounded-full bg-ivory py-3.5 text-sm font-semibold tracking-wide text-ink transition-colors hover:bg-champagne"
            >
              Book Appointment
            </button>
          </div>
        </div>

        <div className="mt-14 flex flex-col items-center justify-between gap-3 border-t border-ivory/10 pt-7 text-xs text-ivory/40 sm:flex-row">
          <p>© {new Date().getFullYear()} {BUSINESS.name}. All rights reserved.</p>
          <p>{BUSINESS.category}</p>
        </div>
      </div>
    </footer>
  );
}
